import { governanceApiClient } from '@/core/infra_api_client'
import type { HostGroup } from '@/core/infra_types'

const BASE_PATH = 'host-groups'
const HOSTS_PATH = 'agent-hosts'

export async function listHostGroups(): Promise<HostGroup[]> {
  const response = await governanceApiClient.get<{ data?: HostGroup[] } | HostGroup[] | null>(BASE_PATH)
  // Older deployments encode an empty Go slice as null. HTTP failures still reject.
  return Array.isArray(response) ? response : response?.data ?? []
}

export async function getHostGroup(id: string): Promise<HostGroup> {
  return governanceApiClient.get<HostGroup>(`${BASE_PATH}/${id}`)
}

interface BootstrapTokenResponse {
  bootstrapToken: string
  bootstrapConfig?: string
  expiresAt: string
  groupId: string
}

/** Generate a long-lived bootstrap token for a host group.
 * Group tokens must survive cluster restarts, scale-ups, and re-bootstraps.
 * Server enforces minimum 30 days; omitting expiresInHours uses the default. */
export async function generateBootstrapToken(groupId: string): Promise<BootstrapTokenResponse> {
  return governanceApiClient.post<BootstrapTokenResponse>(`${BASE_PATH}/${groupId}/bootstrap-token`, {})
}

// ============= Single host lookup =============

export interface AgentHostSummary {
  id: string
  name: string
  status: string
  lastHeartbeatAt?: string
}

/** Fetch a single agent host by ID. Used by the env detail page to surface
 * connection status for single-host environments (no host group). */
export async function getAgentHost(hostId: string): Promise<AgentHostSummary> {
  return governanceApiClient.get<AgentHostSummary>(`${HOSTS_PATH}/${hostId}`)
}

// ============= Pending Hosts (Single Host Provisioning) =============

export interface PendingHost {
  id: string
  name: string
  description?: string
  hostType: 'vpn'
  status: string
  metadata?: Record<string, unknown>
  createdAt: string
}

interface PendingHostsResponse {
  hosts: PendingHost[]
  total: number
}

/** List pending hosts that can be provisioned.
 * Returns hosts with status='pending' that have a cloud_provider in metadata. */
export async function listPendingHosts(): Promise<PendingHost[]> {
  const hosts: PendingHost[] = []
  for (let offset = 0; ; ) {
    const response = await governanceApiClient.get<PendingHostsResponse>(`${HOSTS_PATH}?status=pending&limit=100&offset=${offset}`)
    const page = response.hosts ?? []
    hosts.push(...page)
    if (hosts.length >= response.total) return hosts
    if (!page.length || offset >= 100000) throw new Error('Incomplete pending host lookup')
    offset += page.length
  }
}

interface RegenerateHostTokenResponse {
  bootstrapToken: string
  bootstrapConfig?: string
  expiresAt: string
}

/** Regenerate a bootstrap token for a single host.
 * Use this when provisioning a pending host - the original token may have expired. */
export async function regenerateHostToken(hostId: string): Promise<RegenerateHostTokenResponse> {
  return governanceApiClient.post<RegenerateHostTokenResponse>(`${HOSTS_PATH}/${hostId}/regenerate-token`, {})
}

// ============= Host Updates =============

interface HostUpdateResponse {
  id: string
  status: string
}

/** Promote a host to the Go Intelligence runtime and retire the legacy Rust package
 * after the host has verified the replacement services. */
export async function upgradeHostPackages(hostId: string): Promise<HostUpdateResponse> {
  return governanceApiClient.post<HostUpdateResponse>(`${HOSTS_PATH}/${hostId}/updates`, {
    packages: [
      { name: 'agion-agent-host', version: 'latest' },
      { name: 'agion-mcp', version: 'latest' },
      { name: 'agion-mission-runner', version: 'latest' },
      { name: 'agion-channel-gateway', version: 'latest' },
      { name: 'agion-intelligence-runtime', version: 'latest', retire_legacy_intelligence: true },
      { name: 'agion-alloy', version: 'latest' },
    ],
  })
}
